import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { FeatureFlagName, type MeInformation } from '@api'
import { Command as CommandPrimitive } from 'cmdk'
import { useUnit } from 'effector-react'
import { useLocation, useNavigate } from 'react-router'
import { Link } from 'react-router-dom'

import RestrictedWordsIcon from '@/assets/icons/icon-restricted-words.svg'
import {
  AUTOMATED_MESSAGES_SUB_ITEMS,
  AUTOMATION_SUB_ITEMS,
  LISTS_SUB_ITEMS,
  SIDEBAR_ITEMS,
} from '@/components/layout/sidebar/constants'
import type { NavSingleButtonProps } from '@/components/layout/sidebar/nav/nav-single-button.tsx'
import type { SubRoute } from '@/components/layout/sub-sidebar.tsx'
import { Card } from '@/components/ui/card.tsx'
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSearchBarInput,
} from '@/components/ui/command.tsx'
import {
  $creators,
  $currentCreator,
  $isCreatorProcessCompleted,
} from '@/models/creators.ts'
import { $availableFeatures } from '@/models/feature-flags'
import { hasPermissionsToRoute } from '@/modules/accounts/state/AccessControlGuard.tsx'
import { AppRoutes } from '@/utils/constants.ts'
import { isFeatureAvailable } from '@/utils/featureFlags'
import { canSeeOverview, classNames } from '@/utils/helpers.ts'

import { mapNavigationItem } from './use-sidebar-sections'

export const subItems: Record<string, SubRoute[]> = {
  Automations: AUTOMATION_SUB_ITEMS,
  'Automated Messages': AUTOMATED_MESSAGES_SUB_ITEMS,
  Lists: LISTS_SUB_ITEMS,
  'Blacklisted Words': [
    {
      name: 'Blocked Messages Log',
      href: AppRoutes.RestrictedWords.Logs.asUrl(),
      icon: <RestrictedWordsIcon />,
    },
  ],
}

export const SearchBar = ({
  account,
  collapseSidebar,
}: {
  account: MeInformation | null
  collapseSidebar: (collapsed: boolean) => void
}) => {
  const availableFeatures = useUnit($availableFeatures)
  const activeCreator = useUnit($currentCreator)
  const searchBarInputRef = useRef<HTMLInputElement>(null)
  const searchBarCardRef = useRef<HTMLDivElement>(null)
  const [searchValue, setSearchValue] = useState<string>('')
  const [showCard, setShowCard] = useState<boolean>(false)
  const location = useLocation()

  useEffect(() => {
    setSearchValue('')
    setShowCard(false)
  }, [location])

  useEffect(
    () => {
      const down = (event: KeyboardEvent) => {
        if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
          collapseSidebar(false)
          event.preventDefault()
          searchBarInputRef.current?.focus()
          setShowCard(true)
        }
      }

      const handleClickOutside = (event: MouseEvent) => {
        if (searchValue?.length > 0) {
          return
        }

        // Check if click occurred outside of the search bar and search card
        if (
          searchBarCardRef.current &&
          !searchBarCardRef.current.contains(event.target as Node)
        ) {
          setShowCard(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', down)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', down)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchValue],
  )

  const sidebarItems = useMemo(
    () =>
      availableFeatures?.includes(FeatureFlagName.TASKS_PAGE)
        ? SIDEBAR_ITEMS
        : SIDEBAR_ITEMS.map((section) => ({
            ...section,
            items: section.items.filter((item) => item.title !== 'Tasks'),
          })),
    [availableFeatures],
  )

  function handleSearch(value: string) {
    setSearchValue(value)
    setShowCard(value.length > 0)
  }

  return (
    <CommandPrimitive
      key="search-bar"
      className="relative"
      aria-label="Search"
      ref={searchBarCardRef}
    >
      <CommandSearchBarInput
        data-testid="sidebar-search-bar-input"
        ref={searchBarInputRef}
        placeholder="Search"
        value={searchValue ?? ''}
        onValueChange={handleSearch}
      />
      <div
        className={classNames(
          'z-100 absolute mt-2 w-full rounded-md border',
          !showCard && 'hidden',
        )}
      >
        <Card className="w-full">
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {sidebarItems.map((section) => {
              const sectionItems = section.items.map((item) =>
                mapNavigationItem(item, activeCreator?.id ?? 0),
              )

              return sectionItems.map((value) => {
                if (value.children?.length ?? 0 > 0) {
                  return (
                    <CommandGroup
                      key={value.title}
                      heading={value.title}
                    >
                      {value.children?.map((child) => (
                        <CommandNavItemLink
                          testId="sidebar-search-bar-result-item"
                          key={child.href}
                          account={account}
                          nav={child}
                          keywords={[value.title.toLowerCase()]}
                          collapseSidebar={() => collapseSidebar(true)}
                        />
                      ))}
                    </CommandGroup>
                  )
                }

                return (
                  <CommandNavItemLink
                    testId="sidebar-search-bar-result-item"
                    key={value.title}
                    account={account}
                    nav={value}
                    collapseSidebar={() => collapseSidebar(true)}
                  />
                )
              })
            })}
          </CommandList>
        </Card>
      </div>
    </CommandPrimitive>
  )
}

const CommandNavItemLink = ({
  account,
  nav,
  collapseSidebar,
  keywords,
  testId,
}: {
  account: MeInformation | null
  nav: NavSingleButtonProps
  collapseSidebar: () => void
  keywords?: string[]
  testId?: string
}) => {
  const creators = useUnit($creators)
  const activeCreator = useUnit($currentCreator)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const availableFeatures = useUnit($availableFeatures)

  const subRoutes = subItems[nav.title]

  const hasPermission = useMemo(
    () =>
      !isCreatorProcessCompleted ||
      (nav.title !== 'Overview' &&
        hasPermissionsToRoute(
          activeCreator,
          nav.creatorPermission,
          nav.creatorFeature,
          account,
          nav.accountFeature,
          creators,
          nav.onlyOwner,
          nav.onlyAdmin,
        )) ||
      (nav.title === 'Overview' &&
        canSeeOverview(account, creators, isCreatorProcessCompleted)),
    [isCreatorProcessCompleted, activeCreator, nav, account, creators],
  )

  if (
    (nav.canSee && !nav.canSee(activeCreator, account)) ||
    !hasPermission ||
    !isFeatureAvailable(availableFeatures, nav.featureFlagName)
  ) {
    return
  }

  const link = (
    <CommandLinkItem
      name={nav.title}
      href={nav.href}
      icon={<nav.Icon className="h-4 w-4" />}
      creatorId={activeCreator?.id ?? 0}
      keywords={keywords}
      collapseSidebar={collapseSidebar}
      testId={testId}
    />
  )

  return (
    <>
      {link}
      {subRoutes?.map((subRoute) => (
        <CommandSubRouteItemLink
          testId={testId}
          key={subRoute.href}
          account={account}
          subRoute={subRoute}
          collapseSidebar={collapseSidebar}
          keywords={[...(keywords ?? []), nav.title]}
        />
      ))}
    </>
  )
}

const CommandSubRouteItemLink = ({
  account,
  subRoute,
  keywords,
  collapseSidebar,
  testId,
}: {
  account: MeInformation | null
  subRoute: SubRoute
  keywords?: string[]
  collapseSidebar: () => void
  testId?: string
}) => {
  const creators = useUnit($creators)
  const activeCreator = useUnit($currentCreator)

  const hasPermissions = hasPermissionsToRoute(
    activeCreator,
    subRoute.creatorPermission,
    subRoute.creatorFeature,
    account,
    subRoute.accountFeature,
    creators,
  )

  if (!hasPermissions) {
    return
  }

  return (
    <CommandLinkItem
      testId={testId}
      name={subRoute.name}
      href={subRoute.href}
      icon={subRoute.icon}
      creatorId={activeCreator?.id ?? 0}
      keywords={keywords}
      collapseSidebar={collapseSidebar}
    />
  )
}

const CommandLinkItem = ({
  name,
  href,
  icon,
  creatorId,
  keywords,
  collapseSidebar,
  testId,
}: {
  name: string
  href: string
  creatorId: number
  icon?: React.ReactNode
  keywords?: string[]
  collapseSidebar: () => void
  testId?: string
}) => {
  const navigate = useNavigate()

  const replacedHref = useMemo(
    () => href.replace(':creatorId', String(creatorId)),
    [href, creatorId],
  )

  return (
    <CommandItem
      key={name}
      data-testid={testId}
      keywords={keywords}
      className="flex items-center justify-between px-4 py-3"
      onSelect={() => {
        navigate(replacedHref)
        const activeElement = document.activeElement as HTMLElement | null

        if (activeElement?.blur) {
          activeElement.blur()
        }
      }}
    >
      <Link
        to={replacedHref}
        className="flex w-full items-center gap-2"
        onClick={collapseSidebar}
      >
        {icon}
        <span>{name}</span>
      </Link>
    </CommandItem>
  )
}
